// V4.12 Phase F — side-by-side comparison of 2-4 listings (server-built
// via the compare_listings tool). Premium cards, horizontally scrollable
// on mobile, aligned attribute rows for a real comparison feel.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Car, Home } from "lucide-react";
import { enumLabel } from "../../../utils/enumLabels";

function fmtEur(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}

function CompareThumb({ it }) {
  const [failed, setFailed] = useState(false);
  if (!it.primary_image_url || failed) {
    return (
      <div className="flex h-28 w-full items-center justify-center bg-zinc-800">
        {it.vertical === "auto" ? (
          <Car className="h-6 w-6 text-zinc-600" />
        ) : (
          <Home className="h-6 w-6 text-zinc-600" />
        )}
      </div>
    );
  }
  return (
    <img
      src={it.primary_image_url}
      alt=""
      className="h-28 w-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-zinc-500">{label}</span>
      <span className="text-right text-zinc-200">{value ?? "—"}</span>
    </div>
  );
}

export function ListingCompareWidget({ widget, variant }) {
  const { t } = useTranslation("ai");
  const items = widget.items || [];
  if (items.length < 2) return null;
  const isPublic = variant === "public";
  const CardTag = isPublic ? "div" : Link;
  const isAuto = widget.vertical === "auto";
  const prices = items.map((i) => i.price).filter((p) => typeof p === "number");
  const minPrice = prices.length ? Math.min(...prices) : null;
  return (
    <div
      data-testid="ai-widget-listing-compare"
      className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-2"
    >
      <div className="text-xs uppercase tracking-wide text-zinc-500">
        {t("widgets.compare")} · {items.length} {t("widgets.listingsWord")}
      </div>
      <div className="flex snap-x gap-3 overflow-x-auto pb-1">
        {items.map((it) => (
          <CardTag
            key={it.id}
            {...(isPublic ? {} : { to: `/listing/${it.id}` })}
            data-testid={`ai-compare-card-${it.id}`}
            className={`group min-w-[220px] max-w-[250px] flex-1 snap-start overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950/60 transition ${
              isPublic ? "" : "hover:border-amber-500/50"
            } ${it.offline ? "opacity-60" : ""}`}
          >
            <CompareThumb it={it} />
            <div className="space-y-1.5 p-2.5">
              <div className="line-clamp-2 min-h-[2.2rem] text-xs font-medium text-zinc-100 transition group-hover:text-amber-300">
                {it.title}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-amber-400">
                  {it.price_display || fmtEur(it.price)}
                </span>
                {minPrice != null && it.price === minPrice && (
                  <span className="rounded bg-emerald-500/15 px-1 py-0.5 text-[9px] font-semibold text-emerald-300">
                    {t("widgets.cheapest")}
                  </span>
                )}
                {it.offline && (
                  <span className="rounded bg-zinc-800 px-1 py-0.5 text-[9px] font-semibold uppercase text-zinc-400">
                    {t("widgets.inactive")}
                  </span>
                )}
              </div>
              {typeof it.price_vs_market_pct === "number" && (
                <div
                  className={`text-[10px] font-semibold ${
                    it.price_vs_market_pct < 0 ? "text-emerald-300" : "text-rose-300"
                  }`}
                >
                  {it.price_vs_market_pct > 0 ? "+" : ""}
                  {Math.round(it.price_vs_market_pct)} % {t("widgets.vsMarket")}
                </div>
              )}
              <div className="space-y-1 border-t border-zinc-800/80 pt-1.5">
                {isAuto ? (
                  <>
                    <Row label={t("widgets.year")} value={it.year} />
                    <Row
                      label={t("widgets.mileage")}
                      value={it.mileage_km != null ? `${Math.round(it.mileage_km / 1000)} tkm` : null}
                    />
                    <Row label={t("widgets.power")} value={it.power_ps ? `${it.power_ps} PS` : null} />
                    <Row label={t("widgets.gearbox")} value={it.gearbox ? enumLabel("gearbox", it.gearbox) : null} />
                    <Row label={t("widgets.fuel")} value={it.fuel ? enumLabel("fuel", it.fuel) : null} />
                  </>
                ) : (
                  <>
                    <Row label={t("widgets.rooms")} value={it.rooms} />
                    <Row
                      label={t("widgets.area")}
                      value={it.living_area_m2 ? `${Math.round(it.living_area_m2)} m²` : null}
                    />
                    <Row label="€/m²" value={it.price_per_m2 ? fmtEur(it.price_per_m2) : null} />
                  </>
                )}
                <Row label={t("widgets.location")} value={it.location} />
              </div>
            </div>
          </CardTag>
        ))}
      </div>
    </div>
  );
}
